import { useCallback, useMemo, useRef } from "react";
import { Icon } from "semantic-ui-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ACL_RELATIONSHIPS from "acl-relationships";
import { getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import MapModal from "components/MapModal";
import MachineService from "services/Machine";
import { useMachinesTable } from "hooks/useMachinesTable";
import { MachineTableActionButtons } from "./components/MachineTableActionButtons";
import { MACHINES_TABLE_CONSTANTS } from "./MachinesTableConstants";
import { omitByNil } from "helpers/tables";

const ESTIMATED_TOTAL_EXPLANATION =
  "Counting every matching machine would scan millions of rows, so this total " +
  "is a database estimate. Narrow your filters for an exact count.";

export const renderEstimatedTotalNote = (extra, queryResult) =>
  queryResult?.total_is_estimated ? (
    <Tooltip>
      <TooltipTrigger
        aria-label="Why this total is an estimate"
        className="tw:inline-flex tw:align-middle tw:cursor-help tw:border-0 tw:bg-transparent tw:text-[var(--color-text-secondary)]"
      >
        <Icon name="info circle" fitted />
      </TooltipTrigger>
      <TooltipContent>{ESTIMATED_TOTAL_EXPLANATION}</TooltipContent>
    </Tooltip>
  ) : null;

const MachinesTableV3 = ({
  caseId,
  caseRouteId,
  entityId,
  productLineId,
  tableName = MACHINES_TABLE_CONSTANTS.tableName,
}) => {
  const isStandalone = !caseId && !caseRouteId && !entityId && !productLineId;
  usePageTitle(isStandalone ? MACHINES_TABLE_CONSTANTS.header : null);

  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.machineTags.create,
      ACL_RELATIONSHIPS.machineTags.read,
      ACL_RELATIONSHIPS.machineTags.delete,
    ],
    { partialMatch: true }
  );
  const mapModalRef = useRef(null);

  const openMapModal = useCallback(data => {
    mapModalRef.current.open(data);
  }, []);

  const defaultQueryParams = useMemo(
    () =>
      isStandalone
        ? { complete_table: "true" }
        : omitByNil({
            case_id: caseId,
            entity_id: entityId,
            product_line_id: productLineId,
          }),
    [isStandalone, caseId, entityId, productLineId]
  );
  const { filters, columns, queryKey, invalidateData } = useMachinesTable({
    caseId,
    caseRouteId,
    entityId,
    productLineId,
    isStandalone,
    tableName,
    openMapModal,
  });

  const fetchMachines = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);

        const response = await MachineService.getTable({
          ...nextQueryParams,
          ...defaultQueryParams,
        });
        return response;
      } catch (error) {
        console.error("Error fetching machines:", error);
        throw error;
      }
    },
    [columns, defaultQueryParams]
  );

  return (
    <>
      <MapModal hasAddresses ref={mapModalRef} />

      <BaseTableV3
        columns={columns}
        filters={filters}
        tagsDropdownParams={{ visible: enableTags }}
        queryKey={queryKey}
        className={MACHINES_TABLE_CONSTANTS.className}
        // An unscoped export covers every machine on the tenant, which produces
        // a file too large to open; keep it to the scoped views until there is a
        // way to deliver one that size.
        exportTableName={
          isStandalone ? "" : MACHINES_TABLE_CONSTANTS.exportTableName
        }
        tableName={tableName}
        headerIcon={MACHINES_TABLE_CONSTANTS.headerIcon}
        header={MACHINES_TABLE_CONSTANTS.header}
        enableSearch
        enableSettings
        fetchData={fetchMachines}
        actionButtons={({ selectedIds, selectedData, uncheckAll }) => (
          <MachineTableActionButtons
            selectedIds={selectedIds}
            selectedData={selectedData}
            uncheckAll={uncheckAll}
            invalidateData={invalidateData}
            caseRouteId={caseRouteId}
          />
        )}
        invalidateData={invalidateData}
        downloadParams={defaultQueryParams}
        paginationExtra={renderEstimatedTotalNote}
      />
    </>
  );
};

export default MachinesTableV3;
